/**
 * Utilities for sorting spots based on different criteria
 */

/**
 * Calculate distance between two geographical points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
}

/**
 * Format distance for display
 * @param {number} distance - Distance in kilometers
 * @returns {string} Formatted distance string
 */
export function formatDistance(distance) {
  if (distance == null) return null;
  
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  } else {
    return `${distance.toFixed(1)} km`;
  }
}

/**
 * Calculate distance from user location for each spot and add it to the spot object
 * @param {Array} spots - Array of spot objects
 * @param {Array|null} userLocation - User's location [latitude, longitude] or null
 * @returns {Array} Spots with added distance property
 */
export function enrichSpotsWithDistance(spots, userLocation) {
  if (!userLocation || !Array.isArray(userLocation) || userLocation.length !== 2) {
    // No user location available - add null distance to all spots
    return spots.map(spot => ({
      ...spot,
      distanceFromUser: null,
      distanceFormatted: null
    }));
  }

  return spots.map(spot => {
    if (!spot.latitude || !spot.longitude) {
      return {
        ...spot,
        distanceFromUser: null,
        distanceFormatted: null
      };
    }

    const distance = calculateDistance(
      userLocation[0], 
      userLocation[1], 
      spot.latitude, 
      spot.longitude
    );

    return {
      ...spot,
      distanceFromUser: distance,
      distanceFormatted: formatDistance(distance)
    };
  });
}

/**
 * Sort spots based on selected criteria with optional secondary sorting
 * @param {Array} spots - Array of spot objects (should include distance if proximity sorting)
 * @param {string} sortMode - Primary sorting mode: 'proximity', 'date', 'popularity', 'title'
 * @param {string} sortOrder - Primary sorting order: 'asc' or 'desc'
 * @param {string|null} secondarySortMode - Secondary sorting mode (optional)
 * @param {string} secondarySortOrder - Secondary sorting order: 'asc' or 'desc'
 * @returns {Array} Sorted array of spots
 */
export function sortSpots(spots, sortMode = 'proximity', sortOrder = 'asc', secondarySortMode = null, secondarySortOrder = 'desc') {
  if (!Array.isArray(spots) || spots.length === 0) {
    return spots;
  }

  // Create a copy of the array to avoid mutating the original
  const sortedSpots = [...spots];

  // Helper function to get sort value for a spot based on mode
  const getSortValue = (spot, mode) => {
    switch (mode) {
      case 'proximity':
        return spot.distanceFromUser ?? Number.MAX_SAFE_INTEGER;
      case 'date':
        return spot.created_at ? new Date(spot.created_at) : new Date(0);
      case 'popularity':
        return spot.likes || 0;
      case 'title':
        return (spot.title || '').toLowerCase();
      default:
        return 0;
    }
  };

  // Helper function to compare two values
  const compareValues = (a, b, mode, order) => {
    const aVal = getSortValue(a, mode);
    const bVal = getSortValue(b, mode);

    // Handle null distances for proximity
    if (mode === 'proximity') {
      if (aVal === Number.MAX_SAFE_INTEGER && bVal === Number.MAX_SAFE_INTEGER) return 0;
      if (aVal === Number.MAX_SAFE_INTEGER) return 1;
      if (bVal === Number.MAX_SAFE_INTEGER) return -1;
    }

    let comparison = 0;
    if (mode === 'title') {
      comparison = aVal.localeCompare(bVal, 'fr', { numeric: true, sensitivity: 'base' });
    } else if (mode === 'date') {
      comparison = aVal - bVal;
    } else {
      comparison = aVal - bVal;
    }

    return order === 'desc' ? -comparison : comparison;
  };

  // Sort with primary and optional secondary criteria
  sortedSpots.sort((a, b) => {
    // Primary sort
    const primaryComparison = compareValues(a, b, sortMode, sortOrder);
    
    // If primary values are equal and secondary sorting is enabled, use secondary sort
    if (primaryComparison === 0 && secondarySortMode && secondarySortMode !== sortMode) {
      return compareValues(a, b, secondarySortMode, secondarySortOrder);
    }
    
    return primaryComparison;
  });

  return sortedSpots;
}

/**
 * Get sorted and enriched spots ready for display
 * @param {Array} spots - Raw spots array
 * @param {Array|null} userLocation - User location [lat, lng] or null
 * @param {string} sortMode - Primary sort mode
 * @param {string} sortOrder - Primary sort order
 * @param {string|null} secondarySortMode - Secondary sort mode (optional)
 * @param {string} secondarySortOrder - Secondary sort order
 * @returns {Array} Processed spots ready for display
 */
export function getProcessedSpots(
  spots, 
  userLocation, 
  sortMode = 'proximity', 
  sortOrder = 'asc',
  secondarySortMode = null,
  secondarySortOrder = 'desc'
) {
  // First enrich spots with distance information
  const enrichedSpots = enrichSpotsWithDistance(spots, userLocation);
  
  // Then sort them based on the selected criteria (primary + secondary)
  const sortedSpots = sortSpots(enrichedSpots, sortMode, sortOrder, secondarySortMode, secondarySortOrder);
  
  return sortedSpots;
}

/**
 * Get smart sorting presets for common use cases
 * @returns {Array} Array of preset configurations
 */
export function getSortingPresets() {
  return [
    {
      id: 'discovery',
      name: 'Découverte locale',
      description: 'Spots proches et populaires',
      primary: { mode: 'proximity', order: 'asc' },
      secondary: { mode: 'popularity', order: 'desc' },
      icon: '🔍',
      requiresLocation: true
    },
    {
      id: 'trending',
      name: 'Tendances près de moi',
      description: 'Populaires et accessibles',
      primary: { mode: 'popularity', order: 'desc' },
      secondary: { mode: 'proximity', order: 'asc' },
      icon: '🔥',
      requiresLocation: true
    },
    {
      id: 'newest',
      name: 'Nouveautés locales',
      description: 'Récents et proches',
      primary: { mode: 'date', order: 'desc' },
      secondary: { mode: 'proximity', order: 'asc' },
      icon: '🆕',
      requiresLocation: true
    },
    {
      id: 'alphabetical',
      name: 'Ordre alphabétique',
      description: 'A-Z avec popularité',
      primary: { mode: 'title', order: 'asc' },
      secondary: { mode: 'popularity', order: 'desc' },
      icon: '📝',
      requiresLocation: false
    }
  ];
}